import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../../styles/styles";
import { useSelector } from "react-redux";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import { RxCross1 } from "react-icons/rx";
import { getProductImageUrl } from "../../utils/mediaUtils";
import { useCurrency } from "../../context/CurrencyContext";
import { FiCreditCard, FiLoader, FiLock, FiAlertCircle } from "react-icons/fi";

const Payment = () => {
  const [orderData, setOrderData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state) => state.user);
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();

  useEffect(() => {
    const order = JSON.parse(localStorage.getItem("latestOrder"));
    if (order) {
      setOrderData(order);
    }
  }, []);

  const handleStripePayment = async () => {
    if (loading) return;

    setLoading(true);
    toast.info("Preparing secure checkout... Please wait.");

    const orderPayload = {
      cart: orderData?.cart,
      shippingAddress: orderData?.shippingAddress,
      totalPrice: orderData?.totalPrice,
      subTotalPrice: orderData?.subTotalPrice,
      shippingPrice: orderData?.shipping,
      discountPrice: orderData?.discountPrice,
      couponCode: orderData?.couponCode,
    };

    try {
      const { data } = await axios.post(
        `${server}/payment/stripe/create-checkout-session`,
        orderPayload,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (data.success && data.url) {
        // Redirect to Stripe secure checkout portal
        window.location.href = data.url;
      } else {
        toast.error("Failed to initiate payment session. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Stripe Checkout error:", error);
      toast.error(
        error.response?.data?.message || "Stripe checkout session initiation failed."
      );
      setLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col items-center py-12 min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-[-10%] w-96 h-96 bg-[#1c4980] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
      <div className="absolute top-40 right-[-10%] w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      
      <div className="w-[90%] 1000px:w-[70%] block 800px:flex gap-6 relative z-10">
        {/* Payment options */}
        <div className="w-full 800px:w-[65%]">
          <div className="w-full bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-white/50">
            {/* Header */}
            <div className="flex items-center gap-4 pb-6 border-b border-gray-100 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1c4980] to-[#2a68b5] rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-200">
                <FiCreditCard size={24} />
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 tracking-tight">
                  Secure Checkout
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  Complete your payment securely
                </p>
              </div>
            </div>

            {/* Stripe Card Payment selection */}
            <div className="relative overflow-hidden border border-[#1c4980]/20 bg-blue-50/50 rounded-2xl p-6 mb-8 group hover:border-[#1c4980]/40 transition-colors duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-400/10 rounded-bl-full -z-10 group-hover:scale-110 transition-transform duration-500"></div>
              
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full border-[5px] border-[#1c4980] flex items-center justify-center shadow-sm">
                    <div className="w-1.5 h-1.5 rounded-full bg-white" />
                  </div>
                  <span className="font-bold text-gray-800 text-[16px]">
                    Credit / Debit Card
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg shadow-sm border border-gray-100">
                  <img
                    src="https://js.stripe.com/v3/fingerprinted/img/visa-723719c235.svg"
                    alt="Visa"
                    className="h-5"
                  />
                  <img
                    src="https://js.stripe.com/v3/fingerprinted/img/mastercard-4d88424431.svg"
                    alt="Mastercard"
                    className="h-5"
                  />
                  <img
                    src="https://js.stripe.com/v3/fingerprinted/img/amex-a223282901.svg"
                    alt="Amex"
                    className="h-5"
                  />
                </div>
              </div>

              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                Safe and secure transactions via Stripe. You will be redirected to the secure payment portal. We do not store any card details on our servers.
              </p>

              {/* Secure Shield Note */}
              <div className="flex items-center gap-3 text-sm text-emerald-800 bg-emerald-100/50 border border-emerald-200/50 p-4 rounded-xl">
                <div className="bg-emerald-100 p-1.5 rounded-full text-emerald-600">
                  <FiLock className="flex-shrink-0" size={16} />
                </div>
                <span className="font-medium">SSL Secured 256-bit encrypted checkout</span>
              </div>
            </div>

            {/* Pay Button */}
            <button
              onClick={handleStripePayment}
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-[#1c4980] to-[#2a68b5] hover:from-[#153866] hover:to-[#1e4e8c] text-white rounded-xl text-lg font-bold flex items-center justify-center gap-3 transition-all duration-300 shadow-lg shadow-blue-900/20 disabled:opacity-70 disabled:cursor-not-allowed hover:-translate-y-0.5"
            >
              {loading ? (
                <>
                  <FiLoader className="animate-spin text-white" size={20} />
                  <span>Redirecting to Stripe...</span>
                </>
              ) : (
                <>
                  <FiLock size={18} />
                  <span>Place Order & Pay {formatPrice(orderData?.totalPrice)}</span>
                </>
              )}
            </button>

            {/* Trust Badges */}
            <div className="mt-8 pt-4 border-t border-gray-100 flex justify-center items-center gap-6 text-[11px] text-gray-500 font-medium">
              <span className="flex items-center gap-1.5">
                <FiLock /> PCI-DSS Compliant
              </span>
              <span className="flex items-center gap-1.5">
                🛡️ Stripe Certified Gateway
              </span>
            </div>
          </div>
        </div>

        {/* Cart Review */}
        <div className="w-full 800px:w-[35%] 800px:mt-0 mt-8">
          <CartData orderData={orderData} />
        </div>
      </div>
    </div>
  );
};

const CartData = ({ orderData }) => {
  const { formatPrice } = useCurrency();
  const shipping =
    orderData?.shipping && !isNaN(orderData.shipping)
      ? Number(orderData.shipping).toFixed(2)
      : "0.00";

  return (
    <div className="w-full bg-[#fff] rounded-xl p-5 pb-8 shadow-sm border border-gray-150">
      {/* Order Items Section */}
      {orderData?.cart && orderData.cart.length > 0 && (
        <div className="mb-6">
          <h3 className="text-[16px] font-[600] text-gray-900 mb-4">
            Order Items
          </h3>
          <div className="space-y-4 max-h-[300px] overflow-y-auto">
            {orderData.cart.map((item, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 p-3 border border-gray-100 rounded-xl"
              >
                {/* Product Image */}
                <div className="w-14 h-14 flex-shrink-0">
                  <img
                    src={getProductImageUrl(
                      item.images,
                      0,
                      process.env.REACT_APP_BACKEND_URL
                    )}
                    alt={item.name}
                    className="w-full h-full object-cover rounded-lg border border-[#00000010]"
                    onError={(e) => {
                      e.target.src =
                        "https://placehold.co/64x64/f0f0f0/999999?text=No+Image";
                    }}
                  />
                </div>

                {/* Product Details */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-[13px] font-[500] text-gray-900 truncate">
                    {item.name}
                  </h4>

                  {/* Selected Attributes */}
                  {item.selectedAttributes &&
                    Object.keys(item.selectedAttributes).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {Object.entries(item.selectedAttributes).map(
                          ([key, value]) => (
                            <span
                              key={key}
                              className="inline-block bg-[#f5f5f5] text-[#333] text-[10px] px-1.5 py-0.5 rounded"
                            >
                              {key}: {value}
                            </span>
                          )
                        )}
                      </div>
                    )}

                  {/* Quantity and Price */}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-gray-500">
                      Qty: {item.qty}
                    </span>
                    <span className="text-[13px] font-[600] text-primary-600">
                      {formatPrice(
                        (item.finalPrice ||
                          item.discountedPrice ||
                          item.price ||
                          0) * item.qty
                      )}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <hr className="my-4 border-gray-100" />
        </div>
      )}

      {/* Summary Calculations */}
      <div className="space-y-3 text-sm">
        <div className="flex justify-between text-gray-600">
          <span>Subtotal</span>
          <span className="font-semibold text-gray-900">
            {formatPrice(Number(orderData?.subTotalPrice || 0))}
          </span>
        </div>
        <div className="flex justify-between text-gray-600">
          <span>Shipping fee</span>
          <span className="font-semibold text-gray-900">{formatPrice(shipping)}</span>
        </div>
        {orderData?.discountPrice > 0 && (
          <div className="flex justify-between text-emerald-600 bg-emerald-50/50 p-2 rounded-lg">
            <span>Coupon Discount</span>
            <span className="font-semibold">
              -{formatPrice(orderData.discountPrice)}
            </span>
          </div>
        )}

        <div className="flex justify-between border-t border-gray-150 pt-3 mt-3 text-base">
          <span className="font-bold text-gray-900">Total Price</span>
          <span className="font-black text-primary-600 text-lg">
            {formatPrice(Number(orderData?.totalPrice || 0))}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Payment;
